import { http } from '@/shared/api/axios'
import { normaliseSeatFields } from '../model/seats'
import type {
  BillingPortalSession,
  CheckoutInterval,
  CheckoutSession,
  Cancellation,
  CancellationPreview,
  ChangePlanRequest,
  PlanOption,
  SeatChangeRequest,
  SeatChangeResponse,
  SubscriptionResponse,
} from '../model/types'

const base = '/v1/billing/subscription'

/** Cancellation and data export: the account's lifecycle rather than what it is paying for. */
const accountBase = '/v1/billing/account'

/** Capacity bought on top of the plan. Its own resource: buying adds to it, releasing subtracts. */
const seatsBase = '/v1/billing/seats'

export async function getSubscription(): Promise<SubscriptionResponse> {
  const { data } = await http.get<SubscriptionResponse>(base)
  // Through the normaliser so the two seat fields are real numbers before anything reads them. See
  // `normaliseSeatFields`: without it a backend that has not caught up prints "$NaN.NaN" on the
  // billing page and claims a release the customer never asked for.
  return normaliseSeatFields(data)
}

/**
 * Moves a trialling account to another published plan. Answers with the subscription exactly as
 * `getSubscription` would, so the new caps can go straight into the cache the page reads.
 *
 * `_skipErrorToast` because every refusal here has something specific to say — which plan is too
 * small and by how much, or why this account cannot do it from here — and the interceptor's generic
 * "Please check your input and try again." would bury it. The dialog shows the message instead.
 */
export async function changePlan(request: ChangePlanRequest): Promise<SubscriptionResponse> {
  const { data } = await http.post<SubscriptionResponse>(`${base}/plan`, request, {
    _skipErrorToast: true,
  } as never)
  // Normalised for the same reason as `getSubscription`: this answer goes straight into the cache
  // the page reads, so it has to be the same shape by the time it gets there.
  return normaliseSeatFields(data)
}

/**
 * Buys users beyond what the plan includes. The capacity is available immediately; the new amount
 * appears on the next invoice, and nothing is charged today.
 *
 * `_skipErrorToast` for the reason `changePlan` gives: every refusal here has something specific to
 * say — a plan with no user limit has nothing to sell, a subscription that is ending cannot change
 * its seats — and the interceptor's generic sentence would bury it. The dialogue shows the message.
 */
export async function buySeats(request: SeatChangeRequest): Promise<SeatChangeResponse> {
  const { data } = await http.post<SeatChangeResponse>(`${seatsBase}`, request, {
    _skipErrorToast: true,
  } as never)
  return data
}

/**
 * Gives bought users back, applied at the end of the period already paid for.
 *
 * The body rides on a DELETE, which axios takes as `data`. A quantity in the query string would put
 * a number that changes a bill into logs, caches and browser history.
 *
 * The refusal that matters most here is a release that would leave fewer users than are active: the
 * API names the active count, and that number is the thing that tells the customer what to do, so it
 * must reach the dialogue rather than the interceptor.
 */
export async function releaseSeats(request: SeatChangeRequest): Promise<SeatChangeResponse> {
  const { data } = await http.delete<SeatChangeResponse>(`${seatsBase}`, {
    data: request,
    _skipErrorToast: true,
  } as never)
  return data
}

/**
 * The plans a customer may choose at org creation, cheapest first.
 *
 * Authenticated but not account-scoped: the caller is about to create their first organization, so
 * they have no account and no permissions yet.
 */
export async function getSelectablePlans(): Promise<PlanOption[]> {
  const { data } = await http.get<PlanOption[]>('/v1/billing/plans')
  return data
}

/**
 * What cancelling now would do: when access would stop, on what basis, and when the data would be
 * deleted. Changes nothing.
 *
 * Needs `billing.manage`, not `billing.view` — this is the first step of cancelling, and somebody who
 * may only read the bill should not be shown the mechanics of ending the account.
 */
export async function previewCancellation(): Promise<CancellationPreview> {
  const { data } = await http.get<CancellationPreview>(`${accountBase}/cancellation`)
  return data
}

/**
 * Cancels at the end of the current term.
 *
 * `_skipErrorToast` for the reason `changePlan` gives: a refusal here says something specific
 * ("already scheduled", "already ended") that the interceptor's generic sentence would bury, and the
 * dialog is where the person is looking.
 */
export async function cancelSubscription(): Promise<Cancellation> {
  const { data } = await http.post<Cancellation>(`${accountBase}/cancellation`, undefined, {
    _skipErrorToast: true,
  } as never)
  return data
}

/** Withdraws a cancellation that has not taken effect yet. Refused once it has. */
export async function withdrawCancellation(): Promise<Cancellation> {
  const { data } = await http.delete<Cancellation>(`${accountBase}/cancellation`, {
    _skipErrorToast: true,
  } as never)
  return data
}

/**
 * Nominates the one address this account's billing notices go to. Answers with the whole subscription,
 * exactly as `getSubscription` would.
 *
 * `_skipErrorToast` for the reason `changePlan` gives: a refusal here names what is wrong with the
 * address, or says this account has no subscription record to keep one on, and the interceptor's
 * generic "Please check your input and try again." would bury it. The card shows the message.
 */
export async function setBillingContact(billingEmail: string): Promise<SubscriptionResponse> {
  const { data } = await http.put<SubscriptionResponse>(
    `${base}/billing-contact`,
    { billingEmail },
    { _skipErrorToast: true } as never,
  )
  return data
}

/** Removes it, so notices fall back to everyone holding `billing.manage`. */
export async function clearBillingContact(): Promise<SubscriptionResponse> {
  const { data } = await http.delete<SubscriptionResponse>(`${base}/billing-contact`, {
    _skipErrorToast: true,
  } as never)
  return data
}

/**
 * Creates a Stripe customer-portal session and returns where to send the browser.
 *
 * A POST, not a GET, because the URL it returns is single-use: a prefetch or a link preview of a GET
 * would spend the session before the customer arrived. Needs `billing.manage`, which survives past due
 * and suspension on purpose — a firm that cannot pay must still be able to reach the means of paying.
 *
 * `_skipErrorToast` because the 409 here is not a failure to report as one: it means this account has
 * no payment details with Stripe yet, which is the ordinary state for a legacy or trialling account
 * and needs the card's own sentence rather than a red toast.
 */
/**
 * Opens a Stripe Checkout session and returns where to send the browser.
 *
 * A POST for the same reason the portal's is: the URL is single-use, so a prefetch or a link preview
 * of a GET would spend it before the customer arrived. Needs `billing.manage`.
 *
 * `_skipErrorToast` because none of its refusals are a red toast. A 409 `CHECKOUT_UNAVAILABLE` most
 * often means the account is already subscribed — the state drifted since the page loaded — and the
 * answer to that is to refetch the subscription so the portal replaces this button, not to shout. A
 * 503 means Stripe refused the session, which the card says in its own words.
 */
export async function createCheckoutSession(
  billingInterval: CheckoutInterval,
): Promise<CheckoutSession> {
  const { data } = await http.post<CheckoutSession>(
    '/v1/billing/checkout',
    { billingInterval },
    { _skipErrorToast: true } as never,
  )
  return data
}

export async function createBillingPortalSession(): Promise<BillingPortalSession> {
  const { data } = await http.post<BillingPortalSession>(
    '/v1/billing/portal/session',
    undefined,
    { _skipErrorToast: true } as never,
  )
  return data
}
